import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useEffect } from 'react';
import CloseIcon from './Icons/CloseIcon';
import themeColors from '../assets/styles/themeColors';
import { variables } from '../assets/styles/variables';

const getModalWidth = (variant) => {
  if (variant === 'sm') {
    return '32px';
  }

  if (variant === 'md') {
    return '540px';
  }

  if (variant === 'lg') {
    return '664px';
  }

  if (variant === 'xl') {
    return '865px';
  }
};

const getModalPadding = (variant, noPadding, smallPadding) => {
  if (noPadding) {
    return 0;
  }
  if (smallPadding) {
    return '20px 34px 34px 34px';
  }
  if (variant === 'sm') {
    return '34px';
  }

  if (variant === 'md') {
    return '37px 98px';
  }

  if (variant === 'lg') {
    return '60px 48px';
  }

  if (variant === 'xl') {
    return '42px 45px 45px 37px';
  }
};

export const ModalContainer = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  background-color: ${themeColors.colorBackgroundModal};
`;

const ModalBody = styled.div`
  background: ${themeColors.colorBackgroundPrimary};
  max-width: ${({ variant }) => getModalWidth(variant)};
  max-height: 95vh;
  width: 100%;
  z-index: 1;
  border-radius: 4px;
  outline: none;
  padding: ${({ variant, noPadding, smallPadding }) =>
    getModalPadding(variant, noPadding, smallPadding)};
  position: relative;
  overflow-x: scroll;
  overflow-x: hidden;

  ${({ scrollbarVisible }) =>
    scrollbarVisible &&
    css`
      &::-webkit-scrollbar {
        -webkit-appearance: none;
        width: 7px;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background-color: rgba(0, 0, 0, 0.5);
        -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
      }
    `}

  @media only screen and (max-width: 750px) {
    height: 100vh;
    max-height: 100vh;
    max-width: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: 32px 12px;
  }
`;

export const ModalHeading = styled.h2`
  font-weight: ${(props) => props.fontWeight || 600};
  font-size: ${variables.typography.h.subtitle.fontSize};
  line-height: ${variables.typography.h.subtitle.lineHeight};
  color: ${({ color }) => color || themeColors.colorTextSecondary};
  margin-bottom: ${(props) => (props.mb ? `${props.mb}px` : '10px')};
  align-items: center;
  justify-content: center;
  text-align: ${(props) => (props.center ? `center` : 'left')};
  padding: ${(props) => (props.isVideoModal ? '12px 0 0 24px' : '')};
`;

const LineBelowLabel = styled.hr`
  display: block;
  height: 1px;
  width: 100%;
  border: 0;
  border-top: 1px solid ${themeColors.colorPrimaryLight};
  padding: 0;
`;

const ModalCloseIcon = styled(CloseIcon)`
  position: absolute;
  cursor: pointer;
  top: 0;
  right: 0;
  margin: 20px 20px 0 0;
  z-index: 1;
`;

const ModalContent = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: ${(props) => (props.isVideoModal ? '24px' : '')};
`;

const Modal = ({
  children,
  isOpen,
  onClose,
  label,
  variant = 'md',
  scrollbarVisible = false,
  noPadding,
  smallPadding,
  center,
  removeCloseIcon,
  dataCy,
  dismissOnClickOutside = false,
  isVideoModal,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    onClose(false);
  };

  return ReactDOM.createPortal(
    <ModalContainer onClick={() => dismissOnClickOutside && handleClose()}>
      <ModalBody
        variant={variant}
        noPadding={noPadding}
        smallPadding={smallPadding}
        scrollbarVisible={scrollbarVisible}
        center={center}
        data-cy={dataCy}
        onClick={(e) => e.stopPropagation()}
      >
        {label && (
          <ModalHeading center={center} mb={16} isVideoModal={isVideoModal}>
            {label}
          </ModalHeading>
        )}
        {isVideoModal && <LineBelowLabel />}
        {!removeCloseIcon && <ModalCloseIcon onClick={handleClose} />}
        <ModalContent isVideoModal={isVideoModal}>{children}</ModalContent>
      </ModalBody>
    </ModalContainer>,
    document.getElementById('portal-root'),
  );
};

Modal.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  label: PropTypes.string,
  variant: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  scrollbarVisible: PropTypes.bool,
  noPadding: PropTypes.bool,
  smallPadding: PropTypes.bool,
  center: PropTypes.bool,
  dataCy: PropTypes.string,
  dismissOnClickOutside: PropTypes.bool,
  isVideoModal: PropTypes.bool,
};

export default Modal;
